import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";

const CONTACT_EMAIL = "tamir@people-leads.com";

export default function DataDeletion() {
  const { isAuthenticated, user, logout } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!confirmed) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/me/delete-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        throw new Error("Request failed");
      }

      setSubmitted(true);
    } catch {
      // If the API endpoint isn't available yet, show email fallback
      setError("email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ background: "#0a0a0a", fontFamily: "'Heebo', sans-serif" }}
      dir="ltr"
    >
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-black text-white mb-2">
          Data Deletion Request
        </h1>
        <p className="text-sm text-[rgba(255,255,255,0.4)] mb-10">
          Request deletion of your personal data from Mom3ntum
        </p>

        <div className="space-y-6 text-[rgba(255,255,255,0.7)] leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-white mb-3">
              What Gets Deleted
            </h2>
            <p className="mb-3">
              When you request data deletion, we permanently remove the
              following within 30 days:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Your account profile (name, email, profile picture)</li>
              <li>Survey responses</li>
              <li>Task submissions and points history</li>
              <li>Referral records</li>
              <li>Connected social account data</li>
              <li>Notification history</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">
              How to Request Deletion
            </h2>

            {submitted ? (
              <div
                className="rounded-xl p-6 text-center"
                style={{
                  background: "rgba(200,241,53,0.1)",
                  border: "1px solid rgba(200,241,53,0.3)",
                }}
              >
                <p className="text-white font-bold text-lg mb-2">
                  Request Received
                </p>
                <p>
                  Your data deletion request has been submitted. We will process
                  it within 30 days. You will receive a confirmation email at{" "}
                  <strong className="text-white">{user?.email}</strong>.
                </p>
              </div>
            ) : (
              <>
                <p className="mb-4">
                  <strong>Option 1 — Email us:</strong> Send a deletion request
                  to{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=Data%20Deletion%20Request&body=I%20would%20like%20to%20request%20deletion%20of%20all%20my%20personal%20data%20from%20Mom3ntum.%0A%0AEmail%20on%20account%3A%20${encodeURIComponent(user?.email || "[your email]")}`}
                    className="underline"
                    style={{ color: "#c8f135" }}
                  >
                    {CONTACT_EMAIL}
                  </a>{" "}
                  with the subject "Data Deletion Request" and the email address
                  associated with your account.
                </p>

                {isAuthenticated && (
                  <div>
                    <p className="mb-4">
                      <strong>Option 2 — Submit here:</strong>
                    </p>
                    <div
                      className="rounded-xl p-6"
                      style={{
                        background: "#111",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <p className="mb-4 text-white">
                        Logged in as{" "}
                        <strong>{user?.email}</strong>
                      </p>

                      <label className="flex items-start gap-3 cursor-pointer mb-4">
                        <input
                          type="checkbox"
                          checked={confirmed}
                          onChange={(e) => setConfirmed(e.target.checked)}
                          className="mt-1 w-5 h-5 accent-[#c8f135]"
                        />
                        <span>
                          I understand that this action is irreversible and all
                          my data will be permanently deleted within 30 days.
                        </span>
                      </label>

                      {error === "email" && (
                        <p className="text-sm mb-3" style={{ color: "#FF5D68" }}>
                          Automated deletion is currently unavailable. Please
                          email{" "}
                          <a
                            href={`mailto:${CONTACT_EMAIL}?subject=Data%20Deletion%20Request`}
                            className="underline"
                          >
                            {CONTACT_EMAIL}
                          </a>{" "}
                          to request deletion.
                        </p>
                      )}

                      <button
                        onClick={handleSubmit}
                        disabled={!confirmed || loading}
                        className="px-6 py-3 rounded-full font-bold text-sm transition-opacity"
                        style={{
                          background: confirmed ? "#FF5D68" : "#333",
                          color: confirmed ? "#fff" : "#666",
                          opacity: loading ? 0.6 : 1,
                          cursor: confirmed && !loading ? "pointer" : "not-allowed",
                        }}
                      >
                        {loading ? "Submitting..." : "Request Data Deletion"}
                      </button>
                    </div>
                  </div>
                )}

                {!isAuthenticated && (
                  <p className="mt-4">
                    You can also{" "}
                    <a href="/signin" className="underline" style={{ color: "#c8f135" }}>
                      log in
                    </a>{" "}
                    to submit a deletion request directly.
                  </p>
                )}
              </>
            )}
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">
              Processing Time
            </h2>
            <p>
              Deletion requests are processed within 30 days. You will receive a
              confirmation email once your data has been removed. During the
              processing period, your account will be deactivated.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">
              Facebook / Meta Users
            </h2>
            <p>
              If you signed up using Facebook, you can also request data deletion
              through your{" "}
              <a
                href="https://www.facebook.com/settings?tab=applications"
                className="underline"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#c8f135" }}
              >
                Facebook App Settings
              </a>
              . Remove "Mom3ntum" from your connected apps and we will
              automatically receive a deletion request from Meta.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-3">Contact</h2>
            <p>
              For questions about data deletion or your privacy rights, email us
              at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="underline"
                style={{ color: "#c8f135" }}
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </section>
        </div>

        <div
          className="mt-12 pt-6 text-center text-xs"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.25)",
          }}
        >
          Mom3ntum &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
